import React, { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Animated wireframe AI head hero — a stylized side-profile head built from SVG
 * paths + dot mesh, surrounded by 3-axis orbit rings, floating wireframe
 * cubes, holographic side panels, particles and a glowing platform base.
 *
 * No external assets — pure SVG + framer-motion.
 */

// ─── Tiny helpers ──────────────────────────────────────────────────────────
const RAND_SEED = (i) => Math.sin(i * 9131.7) * 0.5 + 0.5;

const Particle = ({ delay, x, y, color = "#a78bfa", size = 2 }) => (
  <motion.circle
    cx={x}
    cy={y}
    r={size}
    fill={color}
    initial={{ opacity: 0, scale: 0 }}
    animate={{
      opacity: [0, 1, 0],
      scale: [0, 1.4, 0],
      cy: [y, y - 30, y - 60],
    }}
    transition={{ duration: 4 + (delay % 3), delay, repeat: Infinity, ease: "easeOut" }}
    style={{ filter: `drop-shadow(0 0 4px ${color})` }}
  />
);

// Floating wireframe cube — pure CSS 3D transform
const WireCube = ({ size = 42, color = "#7dd3fc", style = {}, delay = 0, dur = 9 }) => {
  const half = size / 2;
  const face = {
    position: "absolute",
    width: size,
    height: size,
    border: `1px solid ${color}`,
    background: `linear-gradient(135deg, ${color}11, transparent)`,
    boxShadow: `0 0 24px -8px ${color}66, inset 0 0 12px -6px ${color}44`,
  };
  return (
    <motion.div
      style={{ position: "absolute", width: size, height: size, transformStyle: "preserve-3d", ...style }}
      animate={{ rotateX: [0, 360], rotateY: [0, 360], y: [0, -10, 0] }}
      transition={{ duration: dur, delay, repeat: Infinity, ease: "linear" }}
    >
      <div style={{ ...face, transform: `translateZ(${half}px)` }} />
      <div style={{ ...face, transform: `rotateY(180deg) translateZ(${half}px)` }} />
      <div style={{ ...face, transform: `rotateY(90deg) translateZ(${half}px)` }} />
      <div style={{ ...face, transform: `rotateY(-90deg) translateZ(${half}px)` }} />
      <div style={{ ...face, transform: `rotateX(90deg) translateZ(${half}px)` }} />
      <div style={{ ...face, transform: `rotateX(-90deg) translateZ(${half}px)` }} />
    </motion.div>
  );
};

// ─── The face: stylized right-facing profile, built as a single bezier path ─
// Coordinates derived to look like a clean cyber-mannequin profile (the kind
// used in the reference image).  All paths live in a 400×500 viewbox.
const FACE_OUTLINE =
  "M 240 60 C 295 60 335 100 345 165 L 348 230 C 352 252 360 268 374 282 C 384 292 386 305 372 310 L 348 318 L 348 345 C 348 372 332 388 305 388 L 282 388 L 282 430 L 232 460";

const FACE_INNER = [
  // Hairline / skull cap
  "M 162 130 C 195 88 240 70 295 78 C 332 84 348 110 350 145",
  // Brow / eye
  "M 252 180 C 268 178 282 184 290 196",
  // Eye crease
  "M 264 190 L 286 192",
  // Cheek line
  "M 282 260 C 296 270 310 274 326 270",
  // Mouth / lip
  "M 300 320 C 312 322 322 320 332 314",
  // Jaw
  "M 230 380 C 258 392 286 392 312 380",
];

// Mesh lines — curved horizontal "topo" lines wrapping the face
const MESH_LINES = [
  "M 150 150 Q 250 130 350 160",
  "M 140 180 Q 250 160 358 195",
  "M 135 215 Q 250 195 362 230",
  "M 135 250 Q 250 230 360 265",
  "M 138 285 Q 250 265 355 300",
  "M 145 320 Q 250 300 348 335",
  "M 158 355 Q 250 335 332 370",
];

function MeshHead() {
  // Generate a deterministic dot mesh along the face
  const dots = useMemo(() => {
    const out = [];
    for (let i = 0; i < 95; i++) {
      const r1 = RAND_SEED(i);
      const r2 = RAND_SEED(i + 99);
      const x = 150 + r1 * 215;
      const y = 100 + r2 * 320;
      // Cull dots outside the rough face area
      const dx = x - 260;
      const dy = y - 240;
      if (dx * dx / (110 * 110) + dy * dy / (170 * 170) > 1.05) continue;
      out.push({ x, y, r: 0.7 + r1 * 1.3, d: r2 * 4 });
    }
    return out;
  }, []);

  return (
    <motion.g
      animate={{ rotateY: [0, 6, -6, 0] }}
      transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "260px 250px", transformBox: "fill-box" }}
    >
      {/* Glow halo behind head */}
      <ellipse cx="260" cy="250" rx="120" ry="180" fill="url(#headGlow)" opacity="0.55" />

      {/* Wireframe topo mesh */}
      {MESH_LINES.map((d, i) => (
        <motion.path
          key={i}
          d={d}
          stroke="url(#meshLine)"
          strokeWidth="0.9"
          fill="none"
          opacity={0.45 + (i % 3) * 0.15}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, delay: 0.1 * i }}
        />
      ))}

      {/* Face outline */}
      <motion.path
        d={FACE_OUTLINE}
        stroke="#a78bfa"
        strokeWidth="1.4"
        fill="none"
        style={{ filter: "drop-shadow(0 0 6px #a78bfa)" }}
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 2.2, ease: "easeInOut" }}
      />

      {/* Inner facial features */}
      {FACE_INNER.map((d, i) => (
        <motion.path
          key={i}
          d={d}
          stroke="#67e8f9"
          strokeWidth="1.1"
          fill="none"
          opacity={0.85}
          style={{ filter: "drop-shadow(0 0 3px #67e8f9)" }}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.4, delay: 0.4 + 0.15 * i }}
        />
      ))}

      {/* Mesh dots */}
      {dots.map((p, i) => (
        <motion.circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={p.r}
          fill={i % 3 === 0 ? "#a78bfa" : "#67e8f9"}
          opacity="0.85"
          animate={{ opacity: [0.4, 0.95, 0.4] }}
          transition={{ duration: 3 + p.d, delay: p.d, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      {/* AI badge over the cheek/forehead */}
      <motion.g
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <rect x="222" y="230" width="56" height="30" rx="4" fill="none" stroke="#67e8f9" strokeWidth="1.2" opacity="0.9" />
        <text
          x="250"
          y="252"
          textAnchor="middle"
          fill="#67e8f9"
          fontSize="20"
          fontWeight="600"
          style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "1px", filter: "drop-shadow(0 0 4px #67e8f9)" }}
        >
          AI
        </text>
      </motion.g>
    </motion.g>
  );
}

// ─── Holographic data panels behind the head ───────────────────────────────
function DataPanel({ x, y, w = 90, h = 56, color = "#7dd3fc", lines = 4, delay = 0 }) {
  return (
    <motion.g
      initial={{ opacity: 0, x: x - 10 }}
      animate={{ opacity: 0.85, x }}
      transition={{ duration: 1.2, delay }}
    >
      <rect x={x} y={y} width={w} height={h} rx="3" fill={`${color}11`} stroke={color} strokeWidth="0.7" opacity="0.7" />
      {[...Array(lines)].map((_, i) => (
        <rect
          key={i}
          x={x + 6}
          y={y + 10 + i * 9}
          width={w * (0.4 + 0.4 * RAND_SEED(i + delay * 7))}
          height="3"
          fill={color}
          opacity="0.55"
        />
      ))}
      <circle cx={x + w - 10} cy={y + 10} r="2.5" fill={color} opacity="0.9" />
    </motion.g>
  );
}

// ─── Glow base platform (the little disc under the head) ───────────────────
function GlowBase() {
  return (
    <motion.g
      animate={{ opacity: [0.7, 1, 0.7] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
    >
      <ellipse cx="262" cy="462" rx="64" ry="11" fill="url(#baseGlow)" />
      <ellipse cx="262" cy="462" rx="58" ry="9" fill="none" stroke="#67e8f9" strokeWidth="1" opacity="0.7" />
      <ellipse cx="262" cy="462" rx="44" ry="6.5" fill="none" stroke="#a78bfa" strokeWidth="0.8" opacity="0.5" />
      <line x1="262" y1="450" x2="262" y2="472" stroke="#67e8f9" strokeWidth="1" opacity="0.5" />
    </motion.g>
  );
}

export default function AIOrbHero() {
  // Memoized particle positions
  const particles = useMemo(() => {
    return Array.from({ length: 22 }).map((_, i) => ({
      id: i,
      x: 80 + RAND_SEED(i) * 360,
      y: 360 + RAND_SEED(i + 33) * 80,
      delay: RAND_SEED(i + 7) * 5,
      color: i % 3 === 0 ? "#67e8f9" : i % 3 === 1 ? "#a78bfa" : "#c4b5fd",
      size: 1 + RAND_SEED(i + 11) * 1.5,
    }));
  }, []);

  return (
    <div
      className="relative aspect-square w-full max-w-[560px] mx-auto"
      style={{ perspective: "1200px" }}
      data-testid="ai-orb-hero"
    >
      {/* Outer soft purple glow */}
      <motion.div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(139,92,246,0.45), rgba(59,130,246,0.18) 60%, transparent 75%)",
          filter: "blur(20px)",
        }}
        animate={{ scale: [1, 1.06, 1], opacity: [0.85, 1, 0.85] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Outer orbit ring — clockwise */}
      <motion.div
        aria-hidden
        className="absolute inset-0 rounded-full border border-purple-500/30"
        style={{ boxShadow: "0 0 60px -20px rgba(139,92,246,0.4) inset" }}
        animate={{ rotate: 360 }}
        transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
      >
        <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-purple-300 shadow-[0_0_18px_4px_rgba(139,92,246,0.7)]" />
        <span className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-cyan-300 shadow-[0_0_14px_3px_rgba(34,211,238,0.7)]" />
        <span className="absolute bottom-2 left-6 w-1 h-1 rounded-full bg-fuchsia-300 shadow-[0_0_10px_3px_rgba(232,121,249,0.7)]" />
      </motion.div>

      {/* Middle dashed counter-orbit */}
      <motion.div
        aria-hidden
        className="absolute inset-[6%] rounded-full"
        style={{ border: "1px dashed rgba(99, 102, 241, 0.45)" }}
        animate={{ rotate: -360 }}
        transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
      />

      {/* Inner ring (tilted) */}
      <motion.div
        aria-hidden
        className="absolute inset-[13%] rounded-full border border-cyan-400/30"
        style={{ transform: "rotateX(60deg)" }}
        animate={{ rotate: 360 }}
        transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
      >
        <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-cyan-300 shadow-[0_0_14px_3px_rgba(34,211,238,0.7)]" />
      </motion.div>

      {/* Glassmorphism circular frame holding the head */}
      <motion.div
        className="absolute inset-[10%] rounded-full overflow-hidden"
        style={{
          background:
            "radial-gradient(closest-side, rgba(15,15,30,0.7), rgba(5,5,15,0.95))",
          border: "1px solid rgba(139,92,246,0.2)",
          backdropFilter: "blur(8px)",
          boxShadow:
            "0 40px 120px -30px rgba(139,92,246,0.6), inset 0 0 40px -12px rgba(34,211,238,0.18)",
        }}
        animate={{ y: [-6, 6, -6] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 500 500" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
          <defs>
            <radialGradient id="headGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.6" />
              <stop offset="55%" stopColor="#3b82f6" stopOpacity="0.25" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <linearGradient id="meshLine" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.1" />
              <stop offset="50%" stopColor="#a78bfa" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#67e8f9" stopOpacity="0.1" />
            </linearGradient>
            <radialGradient id="baseGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.95" />
              <stop offset="60%" stopColor="#a78bfa" stopOpacity="0.35" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <filter id="softGlow">
              <feGaussianBlur stdDeviation="3" />
            </filter>
          </defs>

          {/* Subtle scan lines background */}
          {[...Array(14)].map((_, i) => (
            <line
              key={i}
              x1="40"
              x2="460"
              y1={50 + i * 30}
              y2={50 + i * 30}
              stroke="#3b82f6"
              strokeWidth="0.4"
              opacity="0.08"
            />
          ))}

          {/* Holographic side panels (behind the head) */}
          <DataPanel x="42" y="120" delay={0.1} color="#7dd3fc" />
          <DataPanel x="368" y="120" delay={0.2} color="#a78bfa" />
          <DataPanel x="56" y="318" w={100} h={48} lines={3} delay={0.3} color="#67e8f9" />
          <DataPanel x="350" y="318" w={100} h={48} lines={3} delay={0.4} color="#c4b5fd" />

          {/* The AI head */}
          <MeshHead />

          {/* Base platform */}
          <GlowBase />

          {/* Rising particles */}
          {particles.map((p) => (
            <Particle key={p.id} {...p} />
          ))}
        </svg>
      </motion.div>

      {/* Floating wireframe cubes around the orb */}
      <WireCube size={36} color="#7dd3fc" style={{ top: "16%", left: "8%" }} delay={0} dur={11} />
      <WireCube size={28} color="#a78bfa" style={{ top: "12%", right: "12%" }} delay={1.5} dur={9} />
      <WireCube size={42} color="#67e8f9" style={{ bottom: "18%", right: "6%" }} delay={0.8} dur={13} />
      <WireCube size={26} color="#c4b5fd" style={{ bottom: "26%", left: "10%" }} delay={2.3} dur={10} />

      {/* Floating chip bottom-left ("Live") */}
      <motion.div
        className="absolute -bottom-2 left-2 hidden md:block bx-card px-3 py-2 backdrop-blur-xl bg-[#0B0F19]/80 z-10"
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="flex items-center gap-2">
          <span className="bx-glow-icon cyan" style={{ width: 28, height: 28, borderRadius: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2 3 14h7v8l10-12h-7z" />
            </svg>
          </span>
          <div className="leading-tight">
            <div className="text-[10px] text-slate-400">Live</div>
            <div className="text-xs font-semibold">2,458 gens / hr</div>
          </div>
        </div>
      </motion.div>

      {/* Floating chip top-right ("Latency") */}
      <motion.div
        className="absolute -top-2 right-0 hidden md:block bx-card px-3 py-2 backdrop-blur-xl bg-[#0B0F19]/80 z-10"
        initial={{ y: -12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <div className="flex items-center gap-2">
          <span className="bx-glow-icon" style={{ width: 28, height: 28, borderRadius: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </span>
          <div className="leading-tight">
            <div className="text-[10px] text-slate-400">Latency</div>
            <div className="text-xs font-semibold">120ms</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
